import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fuseAnimations } from '@fuse/animations';
import { ScrumboardService } from '../scrumboard.service';
import { List } from '../list.model';
import { StorageConfiguration } from 'app/shared/common/storage-setting/storage-configuration';
import { ToastrService } from 'ngx-toastr';
import { ParseService } from 'app/shared/common/parse/parse.service';

@Component({
    selector: 'scrumboard-board',
    templateUrl: './board.component.html',
    styleUrls: ['./board.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ScrumboardBoardComponent implements OnInit, OnDestroy {

    board: any;
    private _unsubscribeAll: Subject<any>;
    empRole: any;
    superAccess: boolean = false;
    allowAccess: boolean;
    projectId: any;
    closedCount: any;
    totalCount: any;
    projectName: any;
    workItemsLink: string;
    activeSprintLink: string;
    backlogLink: string;
    sprintLink: string;
    selectedProject: any = { name: '', id: '' };
    boardId: any;
    selectedSprint: { id: any; name: string; startDate: any; endDate: any; status: any; createdAt: any; };
    reportLink: string;
    sprintName: any;
    sprintStartDate: any;
    sprintendDate: any;
    status: any;

    constructor(
        private router: Router,
        private _location: Location,
        private _scrumboardService: ScrumboardService,
        private storageConfiguration: StorageConfiguration,
        private toastr: ToastrService,
        private parseService: ParseService
    ) {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
        this.empRole = this.storageConfiguration.sessionGetItem(this.storageConfiguration.role);
        if (this.empRole == 'Admin') {
            this.superAccess = true;
        }
        if (this.empRole == 'Admin' || this.empRole == 'Leader') {
            this.allowAccess = true
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._scrumboardService.onBoardChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(board => {
                this.board = board;
                // this.totalCount = this.board.cards;
            });
        this.projectId = this.board.project.id;
        this.projectName = this.board.project.get('projectInfo').projectName;
        this.getActiveSprint();

        this.activeSprintLink = '/scrumboard/boards/' + this.board.id + '/' + this.projectName;
        this.backlogLink = '/scrumboard/board/sprint/' + this.projectId + '/' + this.projectName;
        this.sprintLink = '/scrumboard/board/sprint-list/' + this.projectId + '/' + this.projectName;
        this.workItemsLink = '/scrumboard/board/work-items/' + this.projectId + '/' + this.projectName;
        this.reportLink = '/scrumboard/board/reports/' + this.projectId + '/' + this.projectName;

    }

    async getActiveSprint() {
        let queryInstance = this.parseService.instance.Object.extend('Sprint');
        let query = new this.parseService.instance.Query(queryInstance);
        query.include('project');
        query.equalTo('project', { __type: 'Pointer', className: 'Project', objectId: this.projectId });
        query.equalTo('status', 'Active');
        await query.find().then(results => {
            if (results.length > 0) {
                this.sprintName = results[0].get('name');
                this.sprintStartDate = results[0].get('startDate');
                this.sprintendDate = results[0].get('endDate');
                this.status = results[0].get('status');
            }
        });
    }

    sprintDashboard() {
        this.router.navigate(['/scrumboard/board/sprint-dashboard/' + this.projectId + '/' + this.projectName]);
    }

    backlogsLink() {
        this.projectId = this.selectedProject.id ? this.selectedProject.id : this.projectId;
        this.projectName = this.selectedProject.name ? this.selectedProject.name : this.projectName;
        this.router.navigate(['/scrumboard/board/sprint/' + this.projectId + '/' + this.projectName]);
    }

    WorkLink() {
        this.projectId = this.selectedProject.id ? this.selectedProject.id : this.projectId;
        this.projectName = this.selectedProject.name ? this.selectedProject.name : this.projectName;
        this.router.navigate(['/scrumboard/board/work-items/' + this.projectId + '/' + this.projectName]);
    }

    sprintsLink() {
        this.projectId = this.selectedProject.id ? this.selectedProject.id : this.projectId;
        this.projectName = this.selectedProject.name ? this.selectedProject.name : this.projectName;
        this.router.navigate(['/scrumboard/board/sprint-list/' + this.projectId + '/' + this.projectName]);
    }

    reportsLink() {
        this.projectId = this.selectedProject.id ? this.selectedProject.id : this.projectId;
        this.projectName = this.selectedProject.name ? this.selectedProject.name : this.projectName;
        this.router.navigate(['/scrumboard/board/reports/' + this.projectId + '/' + this.projectName]);
    }

    async activeSprint() {
        this.projectId = this.selectedProject.id ? this.selectedProject.id : this.projectId;
        this.projectName = this.selectedProject.name ? this.selectedProject.name : this.projectName;
        let queryInstance = this.parseService.instance.Object.extend('Sprint');
        let query = new this.parseService.instance.Query(queryInstance);
        query.include('project');
        query.equalTo('project', { __type: 'Pointer', className: 'Project', objectId: this.projectId });
        query.equalTo('status', 'Active');
        await query.find().then(results => {
            if (results.length > 0) {
                let project = results[0].get('project').get('projectInfo');
                this.projectName = project.projectName;
                this.boardId = results[0].id;
                this.router.navigate(['/scrumboard/boards/' + this.boardId + '/' + this.projectName]);
            }
            else {
                this.toastr.warning('There is no active sprint');

            }
        });
    }


    backLocation() {
        this.router.navigate(['/scrumboard/board/sprint/' + this.projectId + '/' + this.projectName]);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On list add
     *
     * @param newListName
     */
    onListAdd(newListName): void {
        if (newListName === '') {
            return;
        }

        this._scrumboardService.addList(new List({ name: newListName }));
    }

    /**
     * On board name changed
     *
     * @param newName
     */
    onBoardNameChanged(newName): void {
        this._scrumboardService.updateBoard();
        this._location.go('/pages/scrumboard/boards/' + this.board.id + '/' + this.board.uri);
    }

    /**
     * On drop
     *
     * @param ev
     */
    onDrop(ev): void {
        this._scrumboardService.updateBoard();
    }


}
